
<!-- HERO -->
 <app-header-latest></app-header-latest>
<div class="docs-hero">
  <div class="container-wrapper">
    <div>
      <div class="section-eyebrow">📖 DEVELOPER DOCS</div>
      <h1>Build Faster<br>with <span style="color:#a5b4fc;">NeoHRMZ APIs</span></h1>
      <p>Everything you need to integrate, automate, and extend NeoHRMZ — from quick starts to deep-dive reference material.</p>
      <div style="display:flex;gap:16px;flex-wrap:wrap;">
        <a href="#" class="btn btn-white">Quick Start Guide</a>
        <a href="api-reference.html" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">API Reference →</a>
      </div>
    </div>
    <div>
      <div class="terminal-widget">
        <div class="terminal-widget-bar">
          <div class="terminal-widget-dot" style="background:#ef4444;"></div>
          <div class="terminal-widget-dot" style="background:#fbbf24;"></div>
          <div class="terminal-widget-dot" style="background:#4ade80;"></div>
        </div>
        <div class="terminal-widget-body">
          <div class="t-gray"># Install the NeoHRMZ SDK</div>
          <div class="t-white">npm install <span class="t-green">@NeoHRMZ/sdk</span></div>
          <div style="height:12px;"></div>
          <div class="t-gray"># Initialize the client</div>
          <div class="t-blue">import</div><span class="t-white">  NeoHRMZ  </span><span class="t-blue">from</span><span class="t-yellow"> '@NeoHRMZ/sdk'</span>
          <div class="t-white">const <span class="t-green">hrm</span> = new NeoHRMZ</div>
          <div class="t-white">&nbsp;&nbsp;apiKey: <span class="t-yellow">'hrm_live_••••••••'</span></div>
          <div class="t-white"></div>
          <div style="height:12px;"></div>
          <div class="t-gray"># Fetch all employees</div>
          <div class="t-white">const <span class="t-green">employees</span> = await hrm.<span class="t-blue">employees</span>.<span class="t-yellow">list</span>()</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- QUICK START -->
<section class="quickstart-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">⚡ Quick Start</span>
      <h2 class="sec-h2">Up and Running<br><span class="gradient-text">in 3 Steps</span></h2>
      <p class="sec-sub">From zero to your first API call in under 10 minutes — no backend experience required.</p>
    </div>
    <div class="quickstart-grid">
      <div class="qs-card">
        <div class="qs-card__num">01</div>
        <div class="qs-card__title">Get Your API Key</div>
        <div class="qs-card__desc">Log into your NeoHRMZ account, go to Settings → Developer → API Keys, and create a new key with the required scopes for your use case.</div>
        <a href="#" class="qs-card__link">Go to API Keys →</a>
      </div>
      <div class="qs-card">
        <div class="qs-card__num">02</div>
        <div class="qs-card__title">Install the SDK</div>
        <div class="qs-card__desc">Available for Node.js, Python, Ruby, PHP, Go, and Java. Run the install command and initialize the client with your API key.</div>
        <a href="#" class="qs-card__link">View All SDKs →</a>
      </div>
      <div class="qs-card">
        <div class="qs-card__num">03</div>
        <div class="qs-card__title">Make Your First Call</div>
        <div class="qs-card__desc">Try fetching employee records, processing a payroll run, or syncing attendance data. All endpoints support sandbox mode during development.</div>
        <a href="api-reference.html" class="qs-card__link">Browse Endpoints →</a>
      </div>
    </div>
  </div>
</section>

<!-- DOCS SECTIONS -->
<section class="docs-sections">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">📚 Documentation Sections</span>
      <h2 class="sec-h2">Find What<br><span class="gradient-text">You Need</span></h2>
      <p class="sec-sub">Organized by topic so you can go straight to the guide, reference, or tutorial that helps you most.</p>
    </div>
    <div class="docs-sec-grid">
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">👥</div>
        <div class="docs-sec-card__title">Employees</div>
        <div class="docs-sec-card__count">24 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Create &amp; Update Profiles</div>
          <div class="docs-sec-card__item">Bulk Import via CSV</div>
          <div class="docs-sec-card__item">Custom Fields API</div>
          <div class="docs-sec-card__item">Org Chart Sync</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">💰</div>
        <div class="docs-sec-card__title">Payroll</div>
        <div class="docs-sec-card__count">18 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Running a Payroll Cycle</div>
          <div class="docs-sec-card__item">Payroll Webhooks</div>
          <div class="docs-sec-card__item">Tax Config API</div>
          <div class="docs-sec-card__item">Multi-Entity Payroll</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">🕐</div>
        <div class="docs-sec-card__title">Attendance</div>
        <div class="docs-sec-card__count">15 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Clock In / Out API</div>
          <div class="docs-sec-card__item">Leave Management</div>
          <div class="docs-sec-card__item">Biometric Integration</div>
          <div class="docs-sec-card__item">Overtime Rules</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">🎯</div>
        <div class="docs-sec-card__title">Recruitment</div>
        <div class="docs-sec-card__count">12 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Job Post API</div>
          <div class="docs-sec-card__item">Candidate Webhooks</div>
          <div class="docs-sec-card__item">ATS Integration</div>
          <div class="docs-sec-card__item">Interview Scheduling</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">🔐</div>
        <div class="docs-sec-card__title">Auth &amp; Security</div>
        <div class="docs-sec-card__count">10 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">OAuth 2.0 Flow</div>
          <div class="docs-sec-card__item">API Key Management</div>
          <div class="docs-sec-card__item">SSO Configuration</div>
          <div class="docs-sec-card__item">RBAC &amp; Scopes</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">🔔</div>
        <div class="docs-sec-card__title">Webhooks</div>
        <div class="docs-sec-card__count">8 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Webhook Setup</div>
          <div class="docs-sec-card__item">Event Types</div>
          <div class="docs-sec-card__item">Retry Policy</div>
          <div class="docs-sec-card__item">Signature Verification</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">📊</div>
        <div class="docs-sec-card__title">Analytics</div>
        <div class="docs-sec-card__count">9 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Reports API</div>
          <div class="docs-sec-card__item">Custom Metrics</div>
          <div class="docs-sec-card__item">Data Export</div>
          <div class="docs-sec-card__item">BI Integrations</div>
        </div>
      </div>
      <div class="docs-sec-card">
        <div class="docs-sec-card__icon">🧩</div>
        <div class="docs-sec-card__title">Integrations</div>
        <div class="docs-sec-card__count">20 articles</div>
        <div class="docs-sec-card__items">
          <div class="docs-sec-card__item">Slack &amp; Teams</div>
          <div class="docs-sec-card__item">Google Workspace</div>
          <div class="docs-sec-card__item">QuickBooks / Xero</div>
          <div class="docs-sec-card__item">Custom Integrations</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CODE SAMPLES -->
<section class="code-samples-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">💻 Code Examples</span>
      <h2 class="sec-h2">Copy-Paste<br><span class="gradient-text">Ready Samples</span></h2>
      <p class="sec-sub">Real working examples in every major language to get you to production faster.</p>
    </div>
    <div style="margin-top:48px;">
      <div class="code-tabs">
        <span class="code-tab code-tab--active">Node.js</span>
        <span class="code-tab">Python</span>
        <span class="code-tab">cURL</span>
        <span class="code-tab">Ruby</span>
      </div>
      <div class="code-block">
        <span style="color:#6b7280;">// List all employees with pagination</span><br>
        <span style="color:#60a5fa;">const</span> <span style="color:#e2e8f0;">employees</span> = <span style="color:#60a5fa;">await</span> <span style="color:#4ade80;">hrm</span>.<span style="color:#60a5fa;">employees</span>.<span style="color:#fbbf24;">list</span><br>
        &nbsp;&nbsp;<span style="color:#e2e8f0;">page:</span> <span style="color:#f97316;">1</span>,<br>
        &nbsp;&nbsp;<span style="color:#e2e8f0;">per_page:</span> <span style="color:#f97316;">50</span>,<br>
        &nbsp;&nbsp;<span style="color:#e2e8f0;">status:</span> <span style="color:#fbbf24;">'active'</span>,<br>
        &nbsp;&nbsp;<span style="color:#e2e8f0;">department:</span> <span style="color:#fbbf24;">'engineering'</span><br>
        <br>
        <br>
        <span style="color:#6b7280;">// employees.data → array of employee objects</span><br>
        <span style="color:#6b7280;">// employees.meta.total → total count</span>
      </div>
    </div>
  </div>
</section>

<!-- SDK GRID -->
<section class="sdk-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">🛠️ Official SDKs</span>
      <h2 class="sec-h2">Build in Your<br><span class="gradient-text">Favourite Language</span></h2>
    </div>
    <div class="sdk-grid">
      <div class="sdk-badge"><div class="sdk-badge__icon">🟨</div><div class="sdk-badge__name">JavaScript</div></div>
      <div class="sdk-badge"><div class="sdk-badge__icon">🐍</div><div class="sdk-badge__name">Python</div></div>
      <div class="sdk-badge"><div class="sdk-badge__icon">💎</div><div class="sdk-badge__name">Ruby</div></div>
      <div class="sdk-badge"><div class="sdk-badge__icon">☕</div><div class="sdk-badge__name">Java</div></div>
      <div class="sdk-badge"><div class="sdk-badge__icon">🐘</div><div class="sdk-badge__name">PHP</div></div>
      <div class="sdk-badge"><div class="sdk-badge__icon">🐹</div><div class="sdk-badge__name">Go</div></div>
    </div>
  </div>
</section>
<app-peopleflow-footer></app-peopleflow-footer>



